import { useMemo } from 'react';
import { EventPageType } from '@graphql/generated-base';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetEventCTAButtonsDataQuery } from '../../components/CtaButtons/CtaButtons.graphql.generated';
import { useGetRsvpDeadlineQuery } from '../../components/CtaButtons/RsvpDeadline.graphql.generated';

/**
 * Single source of truth for RSVP deadline gating across guest surfaces.
 *
 * The deadline query is keyed on the event id. We resolve it primarily from
 * `useEventInfo()` (`eventInfo.eventId`), which is seeded from the SSR-embedded
 * `__INITIAL_EVENT_ID__` and stays stable across renders. The handle-keyed
 * `GetEventCTAButtonsData` query (`eventByName.id`) is kept only as a fallback:
 * because it runs `ssr: false`, its result flickers between the id and
 * `undefined` across the many guest surfaces that mount this guard, and keying
 * on it alone made the deadline query re-skip mid-render — the deadline (and
 * the post-deadline button suppression) would drop out whenever it briefly
 * resolved to `undefined`. See LEGRSVP-44.
 */
export const useRsvpDeadlineGuard = () => {
  const [isRsvpDeadlineEnabled] = useIsFeatureEnabled('rsvpDeadlineEnabled');
  const { eventName } = useEventNameMatch();
  const { eventInfo } = useEventInfo();

  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    skip: !eventName,
    ssr: false,
  });
  const eventId = eventInfo?.eventId ?? sessionData?.eventByName?.id;

  const { data: deadlineData } = useGetRsvpDeadlineQuery({
    variables: { eventId: eventId || '' },
    batchMode: 'fast',
    skip: !eventId || !isRsvpDeadlineEnabled,
    ssr: false,
  });

  const rsvpDeadline = deadlineData?.getRsvpSettingsByEventId;
  const isRsvpClosedByDeadline = isRsvpDeadlineEnabled && rsvpDeadline?.isOpen === false;
  const showDeadlineToGuest = isRsvpDeadlineEnabled && rsvpDeadline?.showDeadline === true && rsvpDeadline?.deadlineDate != null;
  const deadlineDate = rsvpDeadline?.deadlineDate ?? null;
  // Custom post-deadline note, surfaced only once RSVP is actually closed.
  // Null/empty means no override — guest surfaces render nothing in that case.
  const deadlineNote = isRsvpClosedByDeadline ? rsvpDeadline?.deadlineNote?.trim() || null : null;

  return { isRsvpClosedByDeadline, showDeadlineToGuest, deadlineDate, deadlineNote };
};

/**
 * Drops the RSVP page from a nav pages array once the deadline has passed.
 *
 * Matches the CTA-button behavior (`shouldDisplayRSVPButton && !isRsvpClosedByDeadline`)
 * so the nav link disappears from every guest surface that renders a filtered pages
 * list — Brannan inline nav / sticky nav, EventMenu hamburger, MobileTopBarNav,
 * FooterNav. Behavior matches disabling the "Show RSVP on your website" admin toggle,
 * without persisting a server-side change (LEGRSVP-85).
 *
 * Note: joy-web only owns the in-SPA entry points. Hard navigation to
 * `/:eventHandle/rsvp` is passed through to the legacy app-browser controller in the
 * monorepo (`rsvp` is in its `subPathsToPassThrough`) and must be handled there
 * (tracked as LEGRSVP-95). The one in-SPA path to that route is FooterNav's client-side
 * `NavLink`, which today renders empty page chrome because no layout registers an `rsvp`
 * entry in `pageWidgets`. Post-deadline the filter now removes the link, so a soft click
 * can't reach it either.
 *
 * Also note: the layouts feed this result into page *resolution*, not just nav — Brannan
 * multi-page matches `currentPage` against it and redirects to the default page when
 * nothing matches (LayoutBrannan.tsx). Today that's an improvement (a mid-session
 * `/rsvp` URL resolves to the default page instead of empty chrome); if a real `rsvp`
 * widget is ever added, dropping the page here would bounce a guest off `/rsvp`
 * mid-session when the deadline query resolves.
 *
 * Returns the input array by reference whenever the filter is a no-op — including the
 * common post-deadline case where `filterRsvpPageIfEffectivelyDisabled` upstream has
 * already stripped the RSVP page. Preserves identity through to `useAlohaScroll`, whose
 * `pages`-keyed effect would otherwise re-init the scroll controller mid-session.
 */
export const useHideRsvpPageIfClosedByDeadline = <P extends Readonly<{ type: EventPageType }>>(pages: ReadonlyArray<P>): ReadonlyArray<P> => {
  const { isRsvpClosedByDeadline } = useRsvpDeadlineGuard();
  return useMemo(() => {
    if (!isRsvpClosedByDeadline) {
      return pages;
    }
    const next = pages.filter(page => page.type !== EventPageType.rsvp);
    return next.length === pages.length ? pages : next;
  }, [pages, isRsvpClosedByDeadline]);
};
